import React, { FC, useCallback, useEffect, useState } from 'react'
import { Row, Col, Divider, Layout, Button, Spin } from 'antd'
import cx from 'classnames'
import { initialize, isSubmitting } from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

// assets
import { v4 as uuidv4 } from 'uuid'
import { ReactComponent as DuplicateIcon } from '../../assets/icons/BaseIcons/duplicate-1-icon.svg'
import useBackUrl from '../../hooks/useBackUrl'

// components
import TemplateModal from '../../components/modals/TemplateModal'
import TemplateGeneralSettingsForm from './components/TemplateGeneralSettingsForm'
import ProceduresSettingsForm from '../../components/forms/ProceduresSettingsForm'
import ConfirmationModal from '../../components/modals/ConfirmationModal'

// reducers
import { RootState } from '../../reducers'
import { getDatabases, getProcedures, getQueues } from '../../reducers/form/formActions'
import { getTemplate } from '../../reducers/templates/templateActions'

// utils
import { deleteReq, patchReq, postReq } from '../../utils/request'
import { FORM } from '../../utils/enums'

// types
import { ITemplate } from '../../types/interfaces'

const { Sider, Content } = Layout

type Props = {}

const TemplateSettingsPage: FC<Props> = () => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const { templateId } = useParams<{ templateId?: string }>()
	const [isConfirmModalShowed, setConfirmModalShowed] = useState(false)
	const [isDuplicatedModalShowed, setDuplicatedModalShowed] = useState(false)
	const [processing, setProcessing] = useState(false)
	const formSubmitting = useSelector((state: RootState) => isSubmitting(FORM.TEMPLATE_GENERAL_SETTINGS)(state))
	const [backUrlLink] = useBackUrl()
	const disable = useSelector((state: RootState) => state.template.disable)
	const data = useSelector((state: RootState) => state.template)

	const initFetch = useCallback(
		async (formTemplateId?: string) => {
			let res = await dispatch(getTemplate(formTemplateId))
			res = { ...res.data, formQueue: res.data?.formQueue?.name }
			if (res?.formDatabase) {
				dispatch(getProcedures(res?.formDatabase))
			}
			dispatch(initialize(FORM.TEMPLATE_GENERAL_SETTINGS, res))
			dispatch(initialize(FORM.PROCEDURES_SETTINGS, res))
		},
		[dispatch]
	)

	useEffect(() => {
		dispatch(getQueues())
		dispatch(getDatabases())
		initFetch(templateId)
	}, [dispatch, initFetch, templateId])

	// update template properties
	const handleSubmit = async (templateData: any) => {
		try {
			const form: ITemplate = {
				name: templateData?.name,
				formDatabase: templateData?.formDatabase,
				formQueue: templateData?.formQueue,
				formType: templateData?.formType,
				description: templateData?.description
			}
			// clear procedures when database change to different
			if (templateData?.formDatabase !== data.template.data?.formDatabase) {
				form.formInputStoredProcedure = ''
				form.formOutputStoredProcedureBeforeSave = ''
				form.formOutputStoredProcedureAfterSave = ''
			} else {
				if (typeof templateData?.formInputStoredProcedure === 'string') {
					form.formInputStoredProcedure = templateData?.formInputStoredProcedure
				}
				if (typeof templateData?.formOutputStoredProcedureBeforeSave === 'string') {
					form.formOutputStoredProcedureBeforeSave = templateData?.formOutputStoredProcedureBeforeSave
				}
				if (typeof templateData?.formOutputStoredProcedureAfterSave === 'string') {
					form.formOutputStoredProcedureAfterSave = templateData?.formOutputStoredProcedureAfterSave
				}
			}
			await patchReq(`/api/admin/templates/${templateId}`, undefined, form)
		} catch (err: any) {
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		} finally {
			await initFetch(templateId)
		}
	}

	const onDeleteConfirm = async () => {
		try {
			setProcessing(true)
			await deleteReq(`/api/admin/templates/${templateId}`, undefined)
			setProcessing(false)
			setConfirmModalShowed(false)
			if (backUrlLink) {
				navigate(backUrlLink)
			}
		} catch (err: any) {
			setProcessing(false)
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		}
	}

	const duplicateTemplate = async (formData: any) => {
		try {
			setProcessing(true)
			const duplicatedForm: ITemplate = {
				name: formData?.name,
				formType: formData?.type,
				description: formData?.description
			}
			const res = await postReq(`/api/admin/templates/${templateId}/duplicate`, undefined, duplicatedForm)
			setDuplicatedModalShowed(false)
			navigate(t(`path:/template-settings/${res?.data?.data?.newTemplateID}${window.location.search}`) as string)
		} catch (err: any) {
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		} finally {
			setProcessing(false)
		}
	}

	const editClass = cx({
		'hide-element': !formSubmitting
	})

	return (
		<>
			<div className={`full-w-placeholder ${editClass}`}>
				<Spin size='large' />
			</div>
			<Sider width={310} className={'settings-sider'} />
			<Content className={'settings-contect'}>
				<div className={'template-setting-form'}>
					<Row gutter={16}>
						<Col className='gutter-row' span={12}>
							<Button
								className={'unfilled-btn w-45 h-10'}
								icon={<DuplicateIcon width={20} height={20} />}
								onClick={() => {
									dispatch(
										initialize(FORM.FORM_TEMPLATE, {
											...data.template?.data,
											name: `${data.template?.data?.name}_copy_${uuidv4()}`,
											type: data.template?.data?.formType
										})
									)
									setDuplicatedModalShowed(true)
								}}
								disabled={disable}
							>
								{t('loc:Duplicate template')}
							</Button>
						</Col>
						<Col className='gutter-row' span={12}>
							<Button
								type='text'
								className={'btn-error-text-color w-45 h-10 float-right'}
								onClick={() => {
									setConfirmModalShowed(true)
								}}
								disabled={disable}
							>
								{t('loc:Delete template')}
							</Button>
						</Col>
					</Row>
					<Divider />
					<Row gutter={16}>
						<Col className='gutter-row' span={12}>
							<h3>{t('loc:General')}</h3>
						</Col>
						<Col className='gutter-row' span={12}>
							<TemplateGeneralSettingsForm onSubmit={handleSubmit} />
						</Col>
					</Row>
					<Divider />
					<Row gutter={16}>
						<Col className='gutter-row' span={12}>
							<h3>{t('loc:Procedures')}</h3>
						</Col>
						<Col className='gutter-row' span={12}>
							<ProceduresSettingsForm
								inputName={'formInputStoredProcedure'}
								outputBeforeName={'formOutputStoredProcedureBeforeSave'}
								outputAfterName={'formOutputStoredProcedureAfterSave'}
								onSubmit={handleSubmit}
								disable={disable}
							/>
						</Col>
					</Row>
				</div>
			</Content>
			<Sider width={310} className={'settings-sider'} />
			<ConfirmationModal
				className={'delete-form-modal'}
				width={400}
				isVisible={isConfirmModalShowed}
				title={t('loc:Delete template?')}
				message={t('loc: Template will be permanently deleted')}
				onConfirm={onDeleteConfirm}
				onCancel={() => {
					setConfirmModalShowed(false)
				}}
				cancelText={t('loc:Cancel')}
				onConfirmBtnTitle={t('loc:Delete template')}
				classNameBtn={'btn-error-background-color'}
				onConfirmBtnWidth={138}
				disabled={processing}
			/>
			<TemplateModal
				width={460}
				handleSubmit={duplicateTemplate}
				isVisible={isDuplicatedModalShowed}
				title={t('loc:Duplicate template')}
				onCancel={() => {
					setDuplicatedModalShowed(false)
				}}
				cancelText={t('loc:Back')}
				onConfirmBtnTitle={t('loc:Duplicate')}
				disabled={processing}
			/>
		</>
	)
}

export default TemplateSettingsPage
